<script setup lang="ts">
import type { ButtonTheme, ButtonThemeConfig } from "@/util/constants";
import { BUTTON_BASE_CONFIG, BUTTON_STYLE_CONSTANTS, BUTTON_THEMES } from "@/util/constants";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

interface Props {
  currentButtonConfig?: {
    label?: string;
    submitSubText?: string;
  };
}

const props = withDefaults(defineProps<Props>(), {
  currentButtonConfig: () => ({
    label: "",
    submitSubText: "",
  }),
});

const emit = defineEmits<{
  back: [];
  selectTheme: [themeConfig: ButtonThemeConfig];
}>();

/**
 * Handles theme selection and emits the merged configuration
 * @param theme - Selected button theme
 */
const handleThemeSelect = (theme: ButtonTheme): void => {
  // Merge base config with theme-specific config
  const mergedConfig = { ...BUTTON_BASE_CONFIG, ...theme.config };
  emit("selectTheme", mergedConfig);
};

/**
 * Gets the safe color value with fallback
 * @param color - Color string to process
 * @param defaultColor - Default color if processing fails
 * @returns Hex color string
 */
const getSafeColor = (color?: string, defaultColor = "ffffff"): string => {
  if (!color || color.length < 6) {
    return defaultColor;
  }
  return color.slice(0, 6);
};

/**
 * Generates button style object from theme configuration
 * @param themeConfig - Partial theme configuration
 * @returns CSS style object for button
 */
const getButtonStyle = (themeConfig: Partial<ButtonThemeConfig>) => {
  // Merge base config with theme config for styling
  const config = { ...BUTTON_BASE_CONFIG, ...themeConfig } as ButtonThemeConfig;

  return {
    background: config.background || `#${getSafeColor(config.bgColor)}`,
    color: `#${getSafeColor(config.color)}`,
    padding: BUTTON_STYLE_CONSTANTS.PADDING,
    borderWidth: `${config.border || 0}px`,
    borderStyle: config.borderType || "none",
    borderColor: `#${getSafeColor(config.borderColor)}`,
    borderRadius: BUTTON_STYLE_CONSTANTS.BORDER_RADIUS,
    shadow: config.shadow
      ? `${config.shadow.horizontal}px ${config.shadow.vertical}px ${config.shadow.blur}px ${
          config.shadow.spread
        }px #${getSafeColor(config.shadow.color)}`
      : "none",
    fontSize: `${config.fontSize || 14}px`,
    fontWeight: BUTTON_STYLE_CONSTANTS.FONT_WEIGHT,
    fontFamily: config.fontFamily || "Inter",
    height: BUTTON_STYLE_CONSTANTS.HEIGHT,
    minWidth: BUTTON_STYLE_CONSTANTS.MIN_WIDTH,
  };
};

/**
 * Generates sub text style object from theme configuration
 * @param themeConfig - Partial theme configuration
 * @returns CSS style object for sub text
 */
const getSubTextStyle = (themeConfig: Partial<ButtonThemeConfig>) => {
  // Merge base config with theme config for styling
  const config = { ...BUTTON_BASE_CONFIG, ...themeConfig } as ButtonThemeConfig;

  return {
    color: `#${getSafeColor(config.subTextColor)}`,
    fontSize: `${config.subTextFontSize || 12}px`,
    fontWeight: config.subTextWeight || 400,
    fontFamily: config.subTextFontFamily || "Inter",
    marginTop: BUTTON_STYLE_CONSTANTS.MARGIN_TOP,
  };
};
</script>

<template>
  <section class="button-themes-container">
    <!-- Theme options -->
    <div class="themes-content overflow-y-auto px-4">
      <!-- Filled category -->
      <div class="theme-category pb-6">
        <h3 class="mb-3 text-xs font-medium text-gray-600">{{ t("common.filled") }}</h3>
        <div class="theme-grid grid grid-cols-2 gap-4">
          <div v-for="theme in BUTTON_THEMES.filled" :key="theme.id" class="button-preview">
            <div
              class="preview-button cursor-pointer text-center transition-all hover:opacity-90"
              :style="getButtonStyle(theme.config)"
              @click="handleThemeSelect(theme)"
            >
              <div class="button-text">{{ t("common.submit") }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Border category -->
      <div class="theme-category pb-6">
        <h3 class="mb-3 text-xs font-medium text-gray-600">{{ t("common.border") }}</h3>
        <div class="theme-grid grid grid-cols-2 gap-4">
          <div v-for="theme in BUTTON_THEMES.border" :key="theme.id" class="button-preview">
            <div
              class="preview-button cursor-pointer text-center transition-all hover:opacity-90"
              :style="getButtonStyle(theme.config)"
              @click="handleThemeSelect(theme)"
            >
              <div class="button-text">{{ t("common.submit") }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Text Only category -->
      <div class="theme-category">
        <h3 class="mb-3 text-xs font-medium text-gray-600">{{ t("common.textOnly") }}</h3>
        <div class="theme-grid grid grid-cols-2 gap-4">
          <div v-for="theme in BUTTON_THEMES.textOnly" :key="theme.id" class="button-preview">
            <div
              class="preview-button cursor-pointer text-center transition-all hover:opacity-90"
              :style="getButtonStyle(theme.config)"
              @click="handleThemeSelect(theme)"
            >
              <div class="button-text">{{ t("common.submit") }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.button-themes-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
}

.themes-header {
  flex-shrink: 0;
  border-bottom: 1px solid #eaecf0;
}

.themes-content {
  flex: 1;
  padding-top: 16px;
  padding-bottom: 24px;
}

.button-preview {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.preview-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1.2;
  white-space: nowrap;
  width: 100%;
}

.button-text {
  line-height: 20px;
}
</style>
